// statusStore.js
import { makeAutoObservable, runInAction } from 'mobx'
import objectStore from './objectStore'
import { config } from '../config'
import logger from '../utils/logger'
import apiClient from '../utils/apiClient'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import { canManageDictionaries } from '../tenancy/resourcePermissions'

class StatusStore {
	statuses = []
	tags = []
	permissions = null

	constructor() {
		makeAutoObservable(this)
	}

	clearOrganizationState() {
		this.statuses = []
		this.tags = []
		this.permissions = null
	}

	/**
	 * Добавляет новый статус.
	 * newStatus – объект с параметрами статуса, например:
	 * { statusName, statusColor, clientStatusName }
	 */

	// Функция получения тегов для указанного пользователя
	async fetchTags(source) {
		logger.log('🎈 ENDP-31 - fetchTags')
		logger.log('🦠 fetchTags source:', source)

		try {
			const response = await apiClient.get('/tags')
			const { items: tags, permissions } = response.data || {}

			logger.log('🎠 fetchTags tags:', tags)

			runInAction(() => {
				this.tags = Array.isArray(tags) ? tags : []
				this.permissions = permissions || null
			})
		} catch (error) {
			logger.error('Ошибка при получении тегов:', error)
		}
	}

	// удаление тегов
	async deleteTag(tagId) {
		logger.log('🎈 ENDP-34 - deleteTag')
		try {
			const response = await apiClient.delete(`/tags/${tagId}`)
			
			// Ожидаем, что сервер вернёт обновлённый список тегов для пользователя
			const updatedTags = response.data

			runInAction(() => {
				// Обновляем список тегов в текущем сторе
				this.tags = updatedTags

				// Обновляем все объекты в objectStore
				objectStore.objects = objectStore.objects.map(object => {
					if (object.details && object.details.tags) {
						const updatedTags = object.details.tags.filter(
							tag => tag.id !== tagId
						)
						return {
							...object,
							details: {
								...object.details,
								tags: updatedTags,
							},
						}
					}
					return object
				})
			})
		} catch (error) {
			logger.error('Ошибка при удалении тега:', error)
		}
	}

	async updateTag(updatedTag) {
		logger.log('🎈 ENDP-33 - updateTag')
		try {
			const response = await apiClient.put(`/tags/${updatedTag.id}`, updatedTag)
			const savedTag = response.data

			runInAction(() => {
				const index = this.tags.findIndex(t => t.id === savedTag.id)
				if (index !== -1) {
					this.tags[index] = savedTag
				}
			})
		} catch (error) {
			logger.error('Ошибка при обновлении тега:', error)
		}
	}

	async addTag(newTag) {
		logger.log('🎈 ENDP-32 - addTag')
		try {
			const response = await apiClient.post('/tags', newTag)
			const savedTag = response.data
			
			runInAction(() => {
				this.tags.push(savedTag)
			})
		} catch (error) {
			logger.error('Ошибка при добавлении тега:', error)
		}
	}

	async moveStatus(statusId, direction) {
		logger.log('🎈 ENDP-39 - moveStatus')
		try {
			const response = await apiClient.put('/statuses/move', { 
				statusId, 
				direction 
			})
			
			const updatedStatuses = response.data
			
			runInAction(() => {
				this.statuses = updatedStatuses
			})
		} catch (error) {
			logger.error('Ошибка при изменении порядка сортировки:', error)
		}
	}

	// удаление статуса
	async deleteStatus(statusId) {
		logger.log('🎈 ENDP-38 - deleteStatus')
		try {
			const response = await apiClient.delete(`/statuses/${statusId}`)

			// Ожидаем, что сервер вернёт обновлённый список статусов после удаления
			const updatedStatuses = response.data

			runInAction(() => {
				this.statuses = updatedStatuses
			})
		} catch (error) {
			logger.error('Ошибка при удалении статуса:', error)
		}
	}

	// Метод обновления статуса
	async updateStatus(updatedStatus) {
		logger.log('🎈 ENDP-37 - updateStatus')
		try {
			const response = await apiClient.put(
				`/statuses/${updatedStatus.id}`,
				updatedStatus
			)

			const savedStatus = response.data

			// Обновляем статус в массиве statuses
			runInAction(() => {
				const index = this.statuses.findIndex(s => s.id === savedStatus.id)
				if (index !== -1) {
					this.statuses[index] = savedStatus
				}
			})
		} catch (error) {
			logger.error('Ошибка при обновлении статуса:', error)
		}
	}

	async fetchStatuses() {
		logger.log('🎈 ENDP-35 - fetchStatuses')
		try {
			const response = await apiClient.get('/statuses')
			const { items, permissions } = response.data || {}
			runInAction(() => {
				this.statuses = Array.isArray(items) ? items : []
				this.permissions = permissions || null
			})
		} catch (error) {
			logger.error('Ошибка при получении статусов:', error)
		}
	}

	// добавляем новый статус
	async addStatus(newStatus) {
		logger.log('🎈 ENDP-36 - addStatus')
		try {
			// Отправляем запрос на сервер через apiClient
			const response = await apiClient.post('/statuses', newStatus)

			// Предполагаем, что сервер возвращает сохранённый статус
			const savedStatus = response.data
			logger.log('Сохранённый статус:', savedStatus)

			// Обновляем массив статусов
			runInAction(() => {
				this.statuses.push(savedStatus)
			})
		} catch (error) {
			logger.error('Ошибка при сохранении статуса:', error)
		}
	}

	get canManageDictionaries() {
		return canManageDictionaries({ permissions: this.permissions })
	}
}

const statusStore = new StatusStore()
registerTenantScopedReset('statuses-and-tags', () => statusStore.clearOrganizationState())
export default statusStore
